"use client"

import { useState, useEffect, useCallback } from "react"
import { X, AlertCircle, LogOut, RefreshCw } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import { getUserAction } from "../../services/action/authentication"
// Custom hook for session timeout
export const useSessionTimeout = (_, onTimeout = () => {}) => {
  const [isTimingOut, setIsTimingOut] = useState(false)
  const {user} =useSelector((state)=>state.authenticationReducer)
  const [timeLeft, setTimeLeft] = useState(840) // Start from 14 minutes (840 seconds)
  const [activity, setActivity] = useState(Date.now())

  // Reset the timer when user activity is detected
  const dispatch=useDispatch();
  // const navigate=useNavigate();
  const resetTimer = useCallback(() => {
    setActivity(Date.now())
    setIsTimingOut(false)
   
  }, [])


  const currentLocation=window.location.href!=="http://localhost:3001/" && window.location.href!=="http://localhost:3001/" && window.location.href!=="https://portal.clickinpedia.io/";
  useEffect(()=>{
    if(!currentLocation && user){
    dispatch(getUserAction({ hideError:true,errorCb: () => {
      localStorage.clear()
      if(currentLocation){
         window.location.href="https://portal.clickinpedia.io/"
      }
    } }))
  }
  },[isTimingOut, currentLocation, dispatch,user])

  // Start the countdown modal
  const startCountdown = useCallback(() => {
    setTimeLeft(840) // Start countdown from 14 minutes (840 seconds)
    setIsTimingOut(true)
  }, [])

  // Handle user staying logged in
  const stayLoggedIn = useCallback(() => {
    resetTimer()
  }, [resetTimer])

  // Setup activity listeners
  useEffect(() => {
    const activityEvents = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"]

    const handleActivity = () => {
      resetTimer()
    }

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity)
    })

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [resetTimer])

  // Check for inactivity
  useEffect(() => {
    if (isTimingOut) return

    const inactivityCheckInterval = setInterval(() => {
      const inactiveTime = (Date.now() - activity) / 1000

      // Show modal after 1 minute (60 seconds) of inactivity
      if (inactiveTime > 120) {
        startCountdown()
        clearInterval(inactivityCheckInterval)
      }
    }, 10000) // Check every 10 seconds

    return () => {
      clearInterval(inactivityCheckInterval)
    }
  }, [activity, isTimingOut, startCountdown])

  // Handle countdown
  useEffect(() => {
    if (!isTimingOut) return

    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval)
          onTimeout()
          return 0
        }
        return prevTime - 1
      })
    }, 1000)

    return () => {
      clearInterval(countdownInterval)
    }
  }, [isTimingOut, onTimeout])

  return {
    isTimingOut,
    timeLeft,
    startCountdown,
    stayLoggedIn,
    resetTimer,
    setIsTimingOut,
  }
}

// Logout Modal Component
const LogoutCountdownModal = ({ isOpen, timeLeft, onClose, onLogout, onStayLoggedIn, initialTimeInSeconds = 840 }) => {
  const [isVisible, setIsVisible] = useState(false)
  const {user} =useSelector((state)=>state.authenticationReducer)
  // Format time as MM:SS
  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`
  }, [])

  // Calculate progress percentage for the progress bar
  const progressPercentage = (timeLeft / initialTimeInSeconds) * 100

  // Handle animation
  useEffect(() => {
    if (isOpen && user && user?.userType!=="ADMIN" && user?.userType!=="SUPER_ADMIN") {
      setIsVisible(true)
    }
  }, [isOpen,user])

  // Handle closing animation
  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 300) // Match this with the transition duration
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
      style={{ opacity: isVisible ? 1 : 0 }}
      onClick={handleClose}
    >
      {/* Modal Content */}
      <div
        className={`
          w-full max-w-md transform rounded-2xl bg-white shadow-2xl 
          transition-all duration-300 ease-in-out overflow-hidden
          ${isVisible ? "translate-y-0 opacity-100 scale-100" : "translate-y-8 opacity-0 scale-95"}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress bar at the top */}
        <div className="h-1.5 bg-gray-100 w-full">
          <div
            className="h-full bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-1000 ease-linear"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Session Timeout</h3>
          </div>
          <button onClick={handleClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors duration-200">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="mb-6 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-red-50 to-orange-50 mb-4">
              <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-orange-600">
                {formatTime(timeLeft)}
              </div>
            </div>
            <p className="text-gray-600 mb-2">
              You will be automatically logged out in{" "}
              <span className="font-semibold text-red-600">{formatTime(timeLeft)}</span>
            </p>
            <p className="text-sm text-gray-500">For security reasons, your session will expire due to inactivity.</p>
          </div>

          {/* Pulsing animation */}
          <div className="relative flex justify-center mb-6">
            <div className="absolute w-16 h-16 bg-red-100 rounded-full animate-ping opacity-75"></div>
            <div className="relative flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-full">
              <LogOut className="w-6 h-6 text-white" />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              onClick={onStayLoggedIn}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Stay Logged In</span>
            </button>
            <button
              onClick={onLogout}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout Now</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LogoutCountdownModal

